import { MvxFactorStates } from './MetabolicVulnerability';
import { resolveBlueprintSteps } from './mvxBlueprint';

/**
 * Systemic Formulas products per step of Darren Schmidt, DC's 7-Step Blueprint
 * (+ Step O, Optimization). Parallels `mvxBlueprint.ts`: this holds the products
 * per step; `resolveMvxSupplements` zips them with the resolved Blueprint steps so
 * the panel can emphasize the products for steps this patient's MVX states
 * indicate and dim the rest (mirrors the Supplement Protocols panel).
 *
 * SOURCE: Schmidt's own "Systemic Formulas 7 Step Remedies" slides from his
 * 2026-09-20 NEXGEN talk, transcribed in
 * docs/reference/mvx-schmidt-slides-2026-09-20.md. Steps 1, 2, 3, 4, 5, 7 and 0
 * are his lists verbatim, confirmed against the full deck 2026-10-05. His deck
 * has NO Step 6 slide — it runs 1-2-3, 4-5, then 7 and 0 — because he FORGOT to
 * include it (he said so on stage), so Step 6 is still the earlier LabSmarts
 * construction (flagged inline) and is the one step whose products are ours
 * while carrying his name. Before that talk the whole mapping was ours. No dosing.
 *
 * Product NAMES are the supplement catalog's titles (the SF-Supps sheet, cat
 * 500xxx), e.g. "3 – Bactrex" rather than the slide's "3 - Bactrex", so
 * `FurtherConsiderations.supplementLinkByName` resolves each one to the sheet's
 * URL. `link` is that same URL as a fallback for when the catalog isn't passed.
 * Metabo-Shake is the one exception: the catalog carries it by flavor, so the
 * plain name falls through to the slug guess, as the Holobiome kits already do.
 *
 * MVX is not a Section and has no DB categories, so this is a STATIC data module
 * (like `holobiomeKits.ts`) — not the findConsiderations / cat-500xxx path.
 */

export interface MvxSupplement {
  /** Product name as the supplement catalog titles it. */
  name: string;
  /** SF product number(s), e.g. "#491" — no longer populated; kept for the orphaned supplements panel. */
  code?: string;
  /** Short qualifier off the slide, e.g. "organ support" or "Lyme Protocol". */
  note?: string;
  link?: string;
}

export interface MvxStepSupplements {
  /** Matches MVX_BLUEPRINT_STEPS[].num (1–7); 0 = Step O (Optimization). */
  step: number;
  products: MvxSupplement[];
}

// Systemic Formulas product page (WooCommerce): /product/<slug>/. These slugs
// are copied from the catalog rows, not guessed; the micro sign is %c2%b5 there.
const sf = (slug: string): string => `https://systemicformulas.com/product/${slug}/`;

// Products that appear on more than one of Schmidt's step slides.
const CLEAR_1: MvxSupplement = { name: 'Clear 1 µBiomic', link: sf('clear-1-%C2%B5biomic') };
const CLEAR_2: MvxSupplement = { name: 'Clear 2 µBiomic', link: sf('clear-2-%C2%B5biomic') };
const TERRA_UBIOMIC: MvxSupplement = { name: 'Terra µBiomic', link: sf('terra-%c2%b5biomic') };
const TERRA_SUPERFOOD: MvxSupplement = { name: 'Terra Superfood', link: sf('terra-superfood') };
const IMMUNE_SUPERFOOD: MvxSupplement = { name: 'Immune Superfood', link: sf('immune-superfood') };
const IMMUNEGENIC: MvxSupplement = { name: 'ImmuneGenic', link: sf('immunegenic') };
const GOLD: MvxSupplement = { name: 'GOLD – Immune Plus', link: sf('gold') };

/** Schmidt's SF products per step (2026-09-20 slides); Step 6 is still ours. */
export const MVX_STEP_SUPPLEMENTS: MvxStepSupplements[] = [
  {
    step: 1, // Nutrition (our title: Foundational Foods)
    products: [{ name: 'Metabo-Shake', link: sf('metabo-shake') }],
  },
  {
    step: 2, // Energy / Nourishment
    products: [
      { name: 'EnerGenic', link: sf('energenic') },
      { name: 'DetoxiGenic', link: sf('detoxigenic') },
      { name: 'eVENTa', link: sf('eventa') },
      { name: 'MethylGenic', link: sf('methylgenic') },
      { name: '16B – 16 B Vitamins Stress Complex', link: sf('16b') },
    ],
  },
  {
    step: 3, // Drainage
    products: [
      { name: 'ACX – Vitamin Detox', link: sf('acx') },
      { name: 'ACP – Vitamin ACP', link: sf('acp') },
      { name: 'LymphGenic', link: sf('lymphgenic') },
      // "Any organ support:" on the slide — pick by the organ that needs it.
      { name: 'K – Kidney', note: 'organ support', link: sf('k-kidney') },
      { name: 'L – Liver', note: 'organ support', link: sf('l-liver') },
      { name: 'C – Colon', note: 'organ support', link: sf('c-colon') },
      { name: 'R – Lung', note: 'organ support', link: sf('r-lung') },
    ],
  },
  {
    step: 4, // Intestinal Cleansing (our title: Intestines & Immune Cleansing)
    products: [
      CLEAR_1,
      CLEAR_2,
      { name: 'VRM1', link: sf('vrm1') },
      { name: 'VRM2', link: sf('vrm2') },
      TERRA_SUPERFOOD,
      TERRA_UBIOMIC,
    ],
  },
  {
    step: 5, // Systemic & Immune Detox (our title: Full-Body Immune Cleansing)
    products: [
      CLEAR_1,
      CLEAR_2,
      // The slide reads "ImmuneGenic (GlycA)": his product for that marker.
      { ...IMMUNEGENIC, note: 'GlycA' },
      { name: 'VRM3', link: sf('vrm3') },
      { name: 'VRM4 – Cell', link: sf('vrm4-cell') },
      { name: '4 – FungDX', link: sf('4-fungdx') },
      { name: '3 – Bactrex', link: sf('3-bactrex') },
      { name: 'VIVI', link: sf('vivi') },
      { name: '5 – Stabilizer', link: sf('5-stabilizer') },
      GOLD,
      { name: 'Cats-A-Tonic', link: sf('cats-a-tonic') },
      { name: 'Immune µBiomic', link: sf('immune-%C2%B5biomic') },
      IMMUNE_SUPERFOOD,
    ],
  },
  {
    // ⚠ NOT from Schmidt — OUR construction (binder + glutathione + DetoxiGenic).
    // His NEXGEN deck jumps Step 5 → Step 7 because he forgot to include the
    // Step 6 slide, which he said on stage; it is an omission, not a judgment
    // that detox belongs to Steps 3 and 5. Replace this the moment he gives the
    // real list. BIND and GCEL are not in the supplement catalog, so they fall
    // through to the slug-guess URL rather than the sheet's.
    step: 6, // Powerful Detoxification
    products: [
      { name: 'BIND', link: sf('bind') },
      { name: 'GCEL', link: sf('gcel') },
      { name: 'DetoxiGenic', link: sf('detoxigenic') },
    ],
  },
  {
    step: 7, // Intracellular Pathogens (our title: Cellular Immune Cleansing)
    products: [
      GOLD,
      { name: 'Tai Ra Chi', link: sf('tai-ra-chi') },
      { name: 'ATAK – Immune Rejuvenator', link: sf('atak') },
      IMMUNEGENIC,
      // "The Lyme Protocol:" on the slide groups these five.
      { ...CLEAR_1, note: 'Lyme Protocol' },
      { ...CLEAR_2, note: 'Lyme Protocol' },
      { name: 'Derma µBiomic', note: 'Lyme Protocol', link: sf('derma-%c2%b5biomic') },
      { name: 'WO – China Healing Oil', note: 'Lyme Protocol', link: sf('wo') },
      { name: 'BindGenic', note: 'Lyme Protocol', link: sf('bindgenic') },
      TERRA_UBIOMIC,
      TERRA_SUPERFOOD,
      IMMUNE_SUPERFOOD,
    ],
  },
  {
    step: 0, // Step O — Optimization (runs throughout)
    products: [
      { name: 'OrganGenic', link: sf('organgenic') },
      { name: 'VitaGenic', link: sf('vitagenic') },
      { name: 'CellGenic', link: sf('cellgenic') },
      { name: 'RPM Omega', link: sf('rpm-omega') },
    ],
  },
];

export interface MvxSupplementStep {
  /** 1–7, or 0 for Step O (Optimization). */
  step: number;
  title: string;
  focus: string;
  /** True when this patient's MVX states indicate the step (Step O = always). */
  indicated: boolean;
  products: MvxSupplement[];
}

// Step O has no Blueprint trigger — Optimization runs throughout every step.
const OPTIMIZATION = {
  title: 'Optimization',
  focus: 'Minerals, vitamins and glandulars — supportive throughout every step.',
};

/**
 * Zip the SF product lists with the resolved Blueprint steps: each step carries
 * its title/focus and whether the patient's states indicate it (emphasized) or
 * not (dimmed). Step O is always indicated (runs throughout). Pure.
 */
export function resolveMvxSupplements(states: MvxFactorStates): MvxSupplementStep[] {
  const steps = resolveBlueprintSteps(states);
  return MVX_STEP_SUPPLEMENTS.map(entry => {
    if (entry.step === 0) {
      return { step: 0, title: OPTIMIZATION.title, focus: OPTIMIZATION.focus, indicated: true, products: entry.products };
    }
    const bp = steps.find(s => s.num === entry.step);
    return {
      step: entry.step,
      title: bp ? bp.title : `Step ${entry.step}`,
      focus: bp ? bp.focus : '',
      indicated: bp ? bp.indicated : false,
      products: entry.products,
    };
  });
}
